import * as React from 'react';
import SingleNavLink from './SingleNavLink';
import {GET_LIST} from '../../fetchApi/types';
import {
    accessTokenLocalStorageKey,
    dataIsValidAfterDelete,
    dataIsValidAfterSave,
    dataValidityKey,
    realtimeDataReducerKey,
    resourceApiKey,
    resourceCustomUsers,
    resourceDocuments,
    resourceDocumentTypes,
    resourceExportConfig,
    resourceIntegrations,
    resourceMenuDocumentTypes,
    resourcePlan,
    resourceRequests,
    resourceRuleConfig,
    resourceRules,
    resourceSuppliers,
    resourceTags,
    resourceTemplateCollections,
    resourceTemplates,
    resourceUserHistory,
    resourceUsers,
    roleAdmin,
    roleOperator,
    roleUser,
} from '../../constants';
import {MenuStateShape} from './menuTypes';
import {connect} from 'react-redux';
import {compose, Dispatch} from 'redux';
import {replaceData} from '../../actions/DataActions';
import {
    CloudCircle as IntegrationsIcon,
    Description as DescriptionIcon,
    Flag as FlagIcon,
    Flag as TemplateCollectionsIcon,
    InsertChart as RequestsIcon,
    Layers as LayersIcon, MonetizationOn,
    People,
    PlaylistAddCheck as ListIcon,
    Settings as SettingsIcon,
    Timeline as UserHistoryIcon,
    TrackChanges as TrackChangesIcon,
    VpnKey as VpnKeyIcon,
    Work as DocumentTypeIcon,
} from '@material-ui/icons';
import {get, isEqual} from 'lodash';
import {changeAfterDeleteDataValidity, changeAfterSaveDataValidity} from '../../actions/RealtimeActions';
import DocumentsMenuItem from './DocumentsMenuItem';
import {withDataProvider} from '../../fetchApi/dataProvider';
import {DataProvider} from '../../fetchApi/tsTypes';
import storage from '../../fetchApi/storage';
import MultiNavLink from './MultiNavLink';

interface DocumentTypeShape {
    id: string;
    name: string;
    description: string;
}

interface PropsShape {
    dispatch: Dispatch;
    dataProvider: DataProvider;
    dataIsValid: boolean;
}

class Menu extends React.Component<PropsShape, MenuStateShape> {
    private readonly highestRole: string;

    constructor(props: Readonly<PropsShape>) {
        super(props);
        this.state = {
            documentTypes: [],
        };

        const accessToken = storage.load(accessTokenLocalStorageKey);
        this.highestRole = accessToken && accessToken.highestRole;
    }

    componentDidMount(): void {
        this.fetchDocTypes();
    }

    fetchDocTypes = (): void => {
        const {dispatch, dataProvider} = this.props;

        dataProvider(GET_LIST, resourceMenuDocumentTypes, {
            include: [
                {
                    relation: 'fileFormats'
                }, {
                    relation: 'exportIntegrations', scope: {
                        include: [
                            {relation: 'integrationCredentials'},
                        ],
                    },
                },
            ],
            order: ['weight DESC'],
        })
            .then((data) => {
                const documentTypes = data.data;
                const menuDocumentTypes = documentTypes.map((docType: DocumentTypeShape) => ({
                    title: docType.name,
                    url: `/${resourceDocuments}/${docType.id}`,
                    translateTitle: false,
                    description: docType.description,
                }));

                const {documentTypes: stateDocumentTypes} = this.state;
                if (!isEqual(stateDocumentTypes, menuDocumentTypes)) {
                    this.setState({documentTypes: menuDocumentTypes});
                }

                dispatch(replaceData({keys: resourceDocumentTypes, value: documentTypes}));
            })
            .catch((e) => {
                console.error(e);
            })
            .finally(() => {
                // TODO: rework
                const actionSave = changeAfterSaveDataValidity({resource: resourceDocumentTypes, payload: []});
                const actionDelete = changeAfterDeleteDataValidity({resource: resourceDocumentTypes, payload: true});
                const userActionSave = changeAfterSaveDataValidity({resource: resourceCustomUsers, payload: []});
                const userActionDelete = changeAfterDeleteDataValidity({resource: resourceCustomUsers, payload: true});

                dispatch(actionSave);
                dispatch(actionDelete);
                dispatch(userActionDelete);
                dispatch(userActionSave);
            });
    };

    componentDidUpdate(prevProps: Readonly<PropsShape>): void {
        const {dataIsValid} = this.props;
        if (prevProps && !dataIsValid) {
            this.fetchDocTypes();
        }
    }

    render(): React.ReactElement | null {
        const {documentTypes} = this.state;

        switch (this.highestRole) {
            case roleAdmin:
                return (
                    <React.Fragment>
                        <DocumentsMenuItem
                            icon={ListIcon}
                            title='documents.name'
                            documentTypes={documentTypes}
                        />
                        <SingleNavLink
                            icon={DocumentTypeIcon}
                            title="documentTypes.name"
                            url={`/${resourceDocumentTypes}`}
                        />
                        <SingleNavLink
                            icon={LayersIcon}
                            title='suppliers.name'
                            url={`/${resourceSuppliers}`}
                        />
                        <SingleNavLink
                            icon={TrackChangesIcon}
                            title='templates.name'
                            url={`/${resourceTemplates}`}
                        />
                        <SingleNavLink
                            icon={TemplateCollectionsIcon}
                            title='templateCollections.name'
                            url={`/${resourceTemplateCollections}`}
                        />
                        <SingleNavLink
                            icon={VpnKeyIcon}
                            title='apiKeys.name'
                            url={`/${resourceApiKey}`}
                        />
                        <SingleNavLink
                            title='integrations.name'
                            url={`/${resourceIntegrations}`}
                            icon={IntegrationsIcon}
                        />
                        <SingleNavLink
                            title='rules.name'
                            url={`/${resourceRules}`}
                            icon={DescriptionIcon}
                        />
                        <MultiNavLink
                            icon={SettingsIcon}
                            title='configs.name'
                            subMenus={[
                                {
                                    title: 'configs.export_config.name',
                                    url: `/${resourceExportConfig}`,
                                },
                                {
                                    title: 'configs.rules_config.name',
                                    url: `/${resourceRuleConfig}`,
                                },
                            ]}
                        />
                        <SingleNavLink
                            icon={RequestsIcon}
                            title='requests.name'
                            url={`/${resourceRequests}`}
                        />
                        <SingleNavLink
                            icon={UserHistoryIcon}
                            title='userHistory.name'
                            url={`/${resourceUserHistory}`}
                        />
                        <SingleNavLink
                            icon={People}
                            title='users.name'
                            url={`/${resourceUsers}`}
                        />
                    </React.Fragment>
                );
            case roleUser:
                return (
                    <React.Fragment>
                        <DocumentsMenuItem
                            icon={ListIcon}
                            title='documents.name'
                            documentTypes={documentTypes}
                        />
                        <SingleNavLink
                            icon={LayersIcon}
                            title='suppliers.name'
                            url={`/${resourceSuppliers}`}
                        />
                        <SingleNavLink
                            icon={VpnKeyIcon}
                            title='apiKeys.name'
                            url={`/${resourceApiKey}`}
                        />
                        <SingleNavLink
                            icon={RequestsIcon}
                            title='requests.name'
                            url={`/${resourceRequests}`}
                        />
                        <SingleNavLink
                            title='integrations.name'
                            url={`/${resourceIntegrations}`}
                            icon={IntegrationsIcon}
                        />
                        <SingleNavLink
                            title='rules.name'
                            url={`/${resourceRules}`}
                            icon={DescriptionIcon}
                        />
                        <SingleNavLink
                            icon={FlagIcon}
                            title='tags.name'
                            url={`/${resourceTags}`}
                        />
                        <SingleNavLink
                            icon={MonetizationOn}
                            title='widgets.plan'
                            url={`/${resourcePlan}`}
                        />
                    </React.Fragment>
                );
            case roleOperator:
                return (
                    <React.Fragment>
                        <DocumentsMenuItem
                            icon={ListIcon}
                            title='documents.name'
                            documentTypes={documentTypes}
                        />
                        <SingleNavLink
                            icon={LayersIcon}
                            title='suppliers.name'
                            url={`/${resourceSuppliers}`}
                        />
                        <SingleNavLink
                            icon={TrackChangesIcon}
                            title='templates.name'
                            url={`/${resourceTemplates}`}
                        />
                        <SingleNavLink
                            icon={TemplateCollectionsIcon}
                            title='templateCollections.name'
                            url={`/${resourceTemplateCollections}`}
                        />

                    </React.Fragment>
                );
            default:
                return null;
        }
    }
}

const mapStateToProps = (state: object): object => {
    const dataValidity = get(state, [realtimeDataReducerKey, resourceDocumentTypes, dataValidityKey], null);

    let dataIsValid = true;
    if (dataValidity) {
        dataIsValid = dataValidity[dataIsValidAfterSave] && dataValidity[dataIsValidAfterDelete];
    }

    return {
        dataIsValid,
    };
};

export default compose(connect(mapStateToProps, null), withDataProvider)(Menu);